import { Collapse, Table, Tag, Tooltip } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "src/hooks/redux";
import {
  convertToLocalAndFormatDate,
  formatTime,
  isLeaseDomainApplicationId,
} from "../../utils/Utils";
import SubmitAgainIcon from "../Shared/icons/SubmitAgainIcon";
import ExclamationIcon from "../Shared/icons/ExclamationIcon";
import SubmitCreditScreeningModal from "./SubmitCreditScreeningModal";
import StatusTag from "../Shared/StatusTag";
import {
  Applicant,
  ApplicantType,
  ScreeningVendor,
} from "../../types/Applicant";
import {
  ApplicationStage,
  ApplicationStatusId,
  ReduxApplication,
} from "../../types/Application";
import {
  ScreeningInfo,
  flattenIssuesForRowSpan,
  getGroupScreeningStatusMessage,
  getScreeningStatuses,
  hasActiveFadvOrTransUnionScreening,
  shouldShowGuarantorOnlySecondaryLabel,
} from "src/utils/Screening";
import { ScreeningStatusMap, ScreeningTypes } from "src/types/Screening";
import Chip from "@mui/material/Chip";
import {
  getIsCommunityIssueResolutionFeatureEnabled,
  mapIssueTypeToAlertType,
} from "src/services/AdminDashService";
import type { Configuration } from "src/types/Redux";
import Checkbox from "@mui/material/Checkbox";
import ResolveAlertModal from "./ResolveAlertModal";

interface ScreeningProps {
  application: ReduxApplication;
}

interface ApplicantExtendedScreening extends Applicant {
  onfidoStatus?: string;
  creditStatus?: string;
  creditStatusDetails?: string | null;
  lastUpdated?: string;
  issues?: { [key: string]: any };
}

const Screening = ({ application }: ScreeningProps) => {
  const [applicantData, setApplicantData] = useState([]);
  const [guarantorData, setGuarantorData] = useState([]);
  const notification = useAppSelector(
    (state) => state.applications.notification
  );
  const [formattedApplication, setFormattedApplication] = useState<
    ReduxApplication | undefined
  >();
  const [applicantColumns, setApplicantColumns] = useState([]);
  const [guarantorColumns, setGuarantorColumns] = useState([]);
  const { Panel } = Collapse;
  const [latestDate, setLatestDate] = useState("");
  const [showCreditScreeningModal, setShowCreditScreeningModal] =
    useState(false);
  const [hasScreeningAlerts, setHasScreeningAlerts] = useState(false);
  const [selectedIssueIds, setSelectedIssueIds] = useState<number[]>([]);
  const [showResolveAlertModal, setShowResolveAlertModal] = useState(false);
  const [selectedApplicantForResolve, setSelectedApplicantForResolve] =
    useState<{
      fullName: string;
      applicantId: number;
      issueId: number;
      issueType: string | null;
    } | null>(null);
  // AP2-6506: ApplicationDetails.tsx (the page this renders under) already dispatches
  // getConfigurations() once on mount - configuration is global, page-level state, not
  // per-component. This used to also fetch it here, guarded by `if (!configuration)` against a
  // selector that fell back to a fresh `{}` on every call - since `{}` is truthy, that guard
  // never actually fired, but the fallback's new-object-per-render did cause this component to
  // re-render (and this effect to re-run) on every tick until configuration loaded. Reading the
  // raw (possibly undefined) value instead is both correct and stable.
  const configuration = useAppSelector<Configuration | undefined>(
    (state) => state.applications.configuration
  );

  const isIssueResolutionEnabled = useMemo(
    () =>
      getIsCommunityIssueResolutionFeatureEnabled(
        configuration?.features ?? ({} as Configuration["features"]),
        application?.communityId
      ),
    [configuration, application]
  );

  const isResolveAlertStatusEligible = useMemo(() => {
    const statusId = formattedApplication?.applicationStatusId;
    if (isLeaseDomainApplicationId(String(application?.applicationId ?? ""))) {
      return statusId === ApplicationStatusId.CreditAndIDPending;
    }
    return (
      statusId === ApplicationStatusId.InProgress ||
      statusId === ApplicationStatusId.LeaseReady
    );
  }, [application, formattedApplication?.applicationStatusId]);

  // Rows are one-per-issue now, so the header count needs distinct
  // applicants, not row count.
  const countDistinctApplicants = (rows: any[]) =>
    new Set(rows.map((row) => row.applicantGroupKey)).size;
  const applicantCount = countDistinctApplicants(applicantData);
  const guarantorCount = countDistinctApplicants(guarantorData);

  useEffect(() => {
    if (applicantData?.length > 0 || guarantorData?.length > 0) {
      prepColumns("Adults", applicantCount);
      prepColumns("Guarantors", guarantorCount);
    }
  }, [
    selectedIssueIds,
    isIssueResolutionEnabled,
    formattedApplication?.applicationStatusId,
    isResolveAlertStatusEligible,
    applicantCount,
    guarantorCount,
  ]);

  useEffect(() => {
    application && prepareApplicationData(application);
  }, [application]);

  const prepareApplicationData = (app: ReduxApplication): void => {
    const formattedApplicants = [...app.applicants].map(
      ({ ...applicant }: ApplicantExtendedScreening) => {
        const { applicantScreening } = applicant;
        const screeningStatuses = getScreeningStatuses(
          applicantScreening,
          setLatestDate
        );
        let onfidoStatus: string | null = null;
        let creditStatus: string | null = null;
        let creditStatusDetails: string | null = null;

        if (!screeningStatuses) return applicant;
        const applicantIssues: any = {};
        Object.values<ScreeningInfo>(screeningStatuses).forEach((screening) => {
          if (screening?.issues) {
            screening.issues.map((issue) => {
              if (!applicantIssues[issue.type]) {
                applicantIssues[issue.type] = [issue];
              } else {
                applicantIssues[issue.type].push(issue);
              }
            });
          }
        });

        const ScreeningStatuses = {
          Other: "Not Started",
          Pending: "Pending",
          Approved: "Approved",
          ApprovedWithConditions: "Approved With Conditions",
          ApprovedWithConditionsMet: "AWC, conditions met",
          Retry: "Retry",
          FurtherReview: "Action Required",
          ProblemResolution: "Problem Resolution",
          Declined: "Declined",
          Cancelled: "Cancelled",
          LowAccept: "Low Accept",
          Conditional: "Conditional",
          Error: "Error",
        };
        if (!!screeningStatuses["onfido"]) {
          onfidoStatus =
            ScreeningStatuses[screeningStatuses["onfido"].screeningStatus] ??
            screeningStatuses["onfido"].screeningStatus;
        }

        const creditScreening =
          screeningStatuses["transunion"] || screeningStatuses["fadv"];
        if (!!creditScreening) {
          creditStatus =
            ScreeningStatuses[creditScreening.screeningStatus] ??
            creditScreening.screeningStatus;
          creditStatusDetails = creditScreening.screeningStatusDetails ?? null;
        }
        if (Object.keys(applicantIssues).length > 0) {
          setHasScreeningAlerts(true);
        }
        applicant.onfidoStatus = onfidoStatus || "Not Started"; // status - identity screening outcome
        applicant.creditStatus = creditStatus || ""; // status - credit screening outcome
        applicant.creditStatusDetails = creditStatusDetails;
        applicant.lastUpdated = screeningStatuses["latestDate"]
          ? screeningStatuses["latestDate"]
          : "";
        applicant.issues = Object.keys(applicantIssues).length
          ? applicantIssues
          : null;
        return applicant;
      }
    );

    setFormattedApplication({
      ...app,
      applicants: formattedApplicants as Applicant[],
    });
    prepareApplicantsAndGuarantorsData(
      formattedApplicants as ApplicantExtendedScreening[]
    );
  };

  // Each data row is one (applicant, issue) pair; identity columns use this
  // to merge back across an applicant's rows via rowSpan.
  const mergedCell = (record: any) => ({ rowSpan: record.rowSpan });

  const prepColumns = (title: string, itemCount: number) => {
    const baseColumns: any = [
      {
        title: `${title} (${itemCount})`,
        dataIndex: "fullName",
        key: "fullName",
        width: "20%",
        onCell: mergedCell,
      },
      {
        title: "Credit Screening Status",
        dataIndex: "credit",
        key: "credit",
        onCell: mergedCell,
      },
      {
        title: "Screening Alerts",
        dataIndex: "alerts",
        key: "alerts",
        width: "35%",
      },
    ];

    if (isIssueResolutionEnabled) {
      baseColumns.push({
        title: "Resolve Alert",
        key: "checkbox",
        render: (_: any, record: any) => {
          if (record.issueId == null) return "-";
          const isChecked = selectedIssueIds.includes(record.issueId);
          const isResolved = record.resolved;

          return (
            <Checkbox
              checked={isChecked || isResolved}
              disabled={
                isChecked ||
                isResolved ||
                record.isSoftDelete ||
                !isResolveAlertStatusEligible
              }
              onChange={() => {
                if (!isChecked) {
                  setSelectedApplicantForResolve({
                    fullName: record.fullName,
                    applicantId: record.applicantId,
                    issueId: record.issueId,
                    issueType: record.issueType,
                  });
                  setShowResolveAlertModal(true);
                } else {
                  const newIds = selectedIssueIds.filter(
                    (id) => id !== record.issueId
                  );
                  setSelectedIssueIds(newIds);
                }
              }}
            />
          );
        },
      });
    }

    baseColumns.push(
      {
        title: "ID Fraud Status",
        dataIndex: "identity",
        key: "identity",
        onCell: mergedCell,
      },
      {
        title: "Last Update",
        dataIndex: "modifiedDate",
        key: "modifiedDate",
        onCell: mergedCell,
      }
    );

    if (title === "Adults") setApplicantColumns(baseColumns);
    else if (title === "Guarantors") setGuarantorColumns(baseColumns);
  };

  const prepareApplicantsAndGuarantorsData = (
    applicants: ApplicantExtendedScreening[]
  ) => {
    const arr: any = [];
    const guarantors: any = [];
    const len = applicants.length;
    for (let i = 0; i < len; i++) {
      const tData = applicants[i];
      // some applicationProcess is null or undefined
      const formattedDate = tData.lastUpdated
        ? `${formatTime(tData.lastUpdated)}, ${convertToLocalAndFormatDate(
            tData.lastUpdated
          )}`
        : "";
      const fullName = `${tData?.firstName || ""} ${
        tData?.lastName || ""
      }`.trim();
      const applicantId = tData?.id;
      const sharedRowFields = {
        fullName: (
          <>
            {fullName}
            {tData?.isSoftDelete && (
              <>
                {" "}
                <Chip color="warning" label="Removed" size="small" />
              </>
            )}
          </>
        ),
        credit:
          tData.creditStatus === "Error" ? (
            <Tooltip
              title={tData.creditStatusDetails || "Error"}
              placement="top"
              trigger={["hover", "focus"]}
            >
              <span tabIndex={0}>
                <Tag color="volcano" icon={<ExclamationIcon color="#F5222D" />}>
                  {tData.creditStatus}
                </Tag>
              </span>
            </Tooltip>
          ) : tData.creditStatusDetails ? (
            <Tooltip
              title={tData.creditStatusDetails}
              placement="top"
              trigger={["hover", "focus"]}
            >
              <span tabIndex={0}>{tData.creditStatus ?? ""}</span>
            </Tooltip>
          ) : (
            tData.creditStatus ?? ""
          ),
        identity: tData.onfidoStatus ?? "",
        modifiedDate: formattedDate,
        applicantId,
        // Distinct per loop iteration regardless of whether applicantId is
        // present/unique, so the header count can't under-count applicants.
        applicantGroupKey: `applicant-${i}`,
        isSoftDelete: tData.isSoftDelete || false,
      };

      // Flatten into one row per issue so the alerts/checkbox cells below
      // are real, independently-matched <tr> cells rather than a manual
      // height match.
      const rows = flattenIssuesForRowSpan(tData.issues).map((row) => ({
        ...sharedRowFields,
        key: `${applicantId ?? "unknown"}-${tData?.applicantTypeId ?? "na"}-${
          row.keySuffix
        }`,
        alerts:
          row.issue && row.issueType ? (
            <>
              <span className={`alert-type-${row.issueType.toLowerCase()}`}>
                {row.sameTypeCount > 1
                  ? `${ScreeningTypes[row.issueType] ?? row.issueType} (${
                      row.sameTypeIndex + 1
                    }): `
                  : `${ScreeningTypes[row.issueType] ?? row.issueType}:`}
              </span>
              <span>{` ${row.issue.description}`}</span>
            </>
          ) : (
            "-"
          ),
        issueId: row.issue?.id ?? null,
        issueType: row.issueType,
        resolved: row.issue?.resolved ?? false,
        rowSpan: row.rowSpan,
      }));

      if (
        applicants[i].applicantTypeId === ApplicantType.Applicant ||
        applicants[i].applicantTypeId === ApplicantType.CoTenant
      ) {
        arr.push(...rows);
      } else {
        guarantors.push(...rows);
      }
    }
    setApplicantData(arr);
    setGuarantorData(guarantors);
  };

  const showSubmitAgain = () => {
    const { applicants } = application || {};

    if (!applicants) {
      return false;
    }

    const everyoneInTwoStepScreening = applicants
      .filter((a) => !a.isSoftDelete)
      .every(
        ({ applicationProcess, isSoftDelete }) =>
          applicationProcess &&
          applicationProcess.applicationStageId ===
            ApplicationStage.TwoStepScreening &&
          !isSoftDelete
      );

    const hasNoFadvOrTransUnionScreening = applicants.every(
      ({ applicantScreening, isSoftDelete }) => {
        if (
          applicantScreening !== null &&
          applicantScreening.length > 0 &&
          !isSoftDelete
        ) {
          const hasFadvOrTransUnion =
            hasActiveFadvOrTransUnionScreening(applicantScreening);

          return !hasFadvOrTransUnion;
        }
        return true;
      }
    );

    const hasGuarantorWithCompleteStatus = applicants.some(
      ({ applicantTypeId, applicationProcess, isSoftDelete }) =>
        applicantTypeId === ApplicantType.Guarantor &&
        applicationProcess?.applicationStatusId ===
          ApplicationStatusId.ApplicationComplete &&
        !isSoftDelete
    );

    const allApplicantsAndCoTenantsHaveCreditAndIDReturned = applicants
      .filter(
        ({ applicantTypeId, isSoftDelete }) =>
          (applicantTypeId === ApplicantType.Applicant ||
            applicantTypeId === ApplicantType.CoTenant) &&
          !isSoftDelete
      )
      .every(
        ({ applicationProcess }) =>
          applicationProcess &&
          applicationProcess.applicationStatusId ===
            ApplicationStatusId.CreditAndIDReturned
      );

    return (
      everyoneInTwoStepScreening &&
      (hasNoFadvOrTransUnionScreening ||
        (hasGuarantorWithCompleteStatus &&
          allApplicantsAndCoTenantsHaveCreditAndIDReturned))
    );
  };

  const getPrimaryApplicant = () => {
    if (!formattedApplication) return null;
    return formattedApplication.applicants.find(
      (x) => x.applicantTypeId === ApplicantType.Applicant && !x.isSoftDelete
    );
  };

  const hasFadvRequestSuccess =
    !!notification &&
    notification.type === "INFO" &&
    notification?.message &&
    notification.message.includes("Credit Screening for");

  const groupDecisionStatusLabel =
    formattedApplication?.adminDashApplicationScreeningResultId
      ? ScreeningStatusMap[
          formattedApplication.adminDashApplicationScreeningResultId
        ]
      : "";

  const groupScreeningMessage = getGroupScreeningStatusMessage(
    formattedApplication?.groupScreeningStatusDetails
  );

  const shouldShowGuarantorOnly = shouldShowGuarantorOnlySecondaryLabel({
    isGuarantorOnly: formattedApplication?.isGuarantorOnly,
    adminDashApplicationScreeningResult:
      formattedApplication?.adminDashApplicationScreeningResult,
    adminDashApplicationScreeningResultId:
      formattedApplication?.adminDashApplicationScreeningResultId,
  });

  /**
   * NOTE: Expected values for overAllScreeningStatuses
   *  are "Pending", "Declined" and "Accepted"
   */
  return (
    <div id="avb-screening">
      <Collapse defaultActiveKey={"1"} bordered={false}>
        <Panel header={`SCREENING & ID VERIFICATION OUTCOMES`} key="1">
          <div className="row">
            <div className="blue-card col-sm-12 col-md-4">
              <h3>Group Screening Outcome</h3>
              <p>GROUP DECISION</p>
              {formattedApplication &&
                !!formattedApplication.adminDashApplicationScreeningResultId && (
                  <div className="col tags">
                    <div className="tagPlaceHolder">
                      <StatusTag name={groupDecisionStatusLabel} />
                    </div>
                    {shouldShowGuarantorOnly && (
                      <div className="tagPlaceHolder">
                        <StatusTag name="GUARANTORONLY" />
                      </div>
                    )}
                    {hasScreeningAlerts && (
                      <div className="tagPlaceHolder">
                        <StatusTag name="Screening alert" />
                      </div>
                    )}
                  </div>
                )}
              {groupScreeningMessage && (
                <p className="group-screening-message">
                  {groupScreeningMessage}
                </p>
              )}
              <p>
                TIME RECEIVED
                <span>{latestDate}</span>
              </p>
              {!hasFadvRequestSuccess && showSubmitAgain() && (
                <button
                  className="submit-again"
                  onClick={() => setShowCreditScreeningModal(true)}
                >
                  <SubmitAgainIcon /> Submit again
                </button>
              )}
            </div>
            <div className=" col-sm-12 col-md-8">
              <Table
                dataSource={applicantData}
                rowKey="key"
                columns={applicantColumns}
                pagination={false}
              />
              <Table
                dataSource={guarantorData}
                rowKey="key"
                columns={guarantorColumns}
                pagination={false}
              />
            </div>
          </div>
        </Panel>
      </Collapse>
      <SubmitCreditScreeningModal
        applicationId={
          formattedApplication && formattedApplication.applicationId
        }
        applicant={getPrimaryApplicant()}
        applicationConfiguration={formattedApplication?.configuration}
        configuration={configuration}
        leaseId={application?.leaseId}
        phaseId={application?.phaseId}
        showModal={showCreditScreeningModal}
        setShowModal={setShowCreditScreeningModal}
      />
      {showResolveAlertModal && selectedApplicantForResolve && (
        <ResolveAlertModal
          showModal={showResolveAlertModal}
          setShowModal={setShowResolveAlertModal}
          applicant={{
            fullName: selectedApplicantForResolve?.fullName ?? "",
            applicantId: selectedApplicantForResolve?.applicantId,
            issueId: selectedApplicantForResolve?.issueId ?? 0,
          }}
          phaseId={application?.phaseId}
          leaseId={application?.leaseId}
          applicationId={
            application?.applicationId !== undefined
              ? String(application.applicationId)
              : undefined
          }
          alertType={mapIssueTypeToAlertType(
            selectedApplicantForResolve?.issueType
          )}
          onConfirmResolve={(wasSuccessful: boolean) => {
            if (wasSuccessful) {
              setSelectedIssueIds((prev) => [
                ...prev,
                selectedApplicantForResolve?.issueId,
              ]);
            }
            setShowResolveAlertModal(false);
          }}
          onCancel={() => {
            setShowResolveAlertModal(false);
          }}
        />
      )}
    </div>
  );
};

export default Screening;
